import { Info } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/libs/utils';
import { Text } from '../Text/Text';

export type OtpInputProps = {
  id: string;
  value?: string;
  onChange: (id: string, value: string) => void;
  error?: boolean;
  loading?: boolean;
  previousId?: string | null;
  nextId?: string | null;
};

// Helper functions for OTP input navigation
const focusInput = (inputId: string | null) => {
  if (!inputId) {
    return null;
  }
  const input = document.getElementById(inputId) as HTMLInputElement;
  if (input) {
    input.select();
  }
  return input;
};

const OtpInputItem: React.FC<OtpInputProps> = ({
  id,
  value = '',
  onChange,
  error = false,
  loading = false,
  previousId,
  nextId,
}) => {
  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (loading) {
      return;
    }

    // Navigation only. Submission is driven exclusively by handleInputChange
    // (triggered on the "input"/onChange event) so a completed code is
    // submitted exactly once. Previously this handler also called
    // handleSubmit() on "keyup" for the same keystroke, which — because the
    // change event's re-render had already landed by the time keyup fired —
    // read the freshly-completed pin and fired a second, duplicate submit.
    if (e.key === 'Backspace' || e.key === 'ArrowLeft') {
      focusInput(previousId ?? null);
    } else if (nextId) {
      focusInput(nextId);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (loading) {
      return; // Prevent changes when loading
    }
    onChange(id, e.target.value);
  };

  return (
    <div className="flex justify-center">
      <input
        aria-label={`OTP digit ${id}`}
        autoComplete="one-time-code"
        className={cn(
          'inline-flex w-[49px] items-center justify-center rounded-lg border-2 border-gray-300 p-4 transition-colors focus:border-magenta focus:ring-magenta',
          error && 'border-red-600',
          loading && 'cursor-not-allowed bg-gray-50 opacity-60'
        )}
        disabled={loading}
        id={id}
        inputMode="numeric"
        maxLength={1}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        pattern="[0-9a-zA-Z]*"
        readOnly={loading}
        type="text"
        value={value}
      />
    </div>
  );
};

// Loading spinner component
const LoadingSpinner = () => (
  <div className="mt-4 flex items-center justify-center">
    <div className="flex items-center space-x-2">
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-magenta border-t-transparent" />
      <Text color="text-gray-600" size="text-sm">
        Verifying code...
      </Text>
    </div>
  </div>
);

type InputValuesProps = {
  [key: string]: string;
};

type OtpInputGroupProps = {
  handleOtpSubmit: (inputValues: string) => void;
  valueInput?: string;
  error?: boolean;
  errorMessage?: string;
  length?: number;
  loading?: boolean;
  className?: string;
};

const OtpInput = ({
  handleOtpSubmit,
  valueInput,
  error,
  errorMessage,
  length = 4,
  loading = false,
  className,
}: OtpInputGroupProps) => {
  const [inputValues, setInputValues] = useState<InputValuesProps>({
    input1: valueInput ? valueInput?.charAt(0) : '',
    input2: valueInput ? valueInput?.charAt(1) : '',
    input3: valueInput ? valueInput?.charAt(2) : '',
    input4: valueInput ? valueInput?.charAt(3) : '',
  });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Guards against submitting the exact same completed code more than once
  // (e.g. a stray double-fire of the completion check). Comparing by value —
  // rather than a plain "already submitted" boolean — still allows a
  // genuinely new code (a fresh paste, or a retry once the consumer resets
  // by toggling `loading`) to go through.
  const lastSubmittedPinRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loading) {
      lastSubmittedPinRef.current = null;
    }
  }, [loading]);

  const submitPinOnce = useCallback(
    (pin: string) => {
      if (
        pin.length !== length ||
        loading ||
        lastSubmittedPinRef.current === pin
      ) {
        return;
      }
      lastSubmittedPinRef.current = pin;
      handleOtpSubmit(pin);
    },
    [handleOtpSubmit, length, loading]
  );

  const handleInputChange = (inputId: string, value: string) => {
    if (loading) {
      return; // Prevent changes when loading
    }

    setInputValues((prevInputValues) => {
      const newInputValues = { ...prevInputValues, [inputId]: value };
      return newInputValues;
    });

    // Check if we should submit after state update
    const newInputValues = { ...inputValues, [inputId]: value };
    const pin = Object.values(newInputValues).join('');
    submitPinOnce(pin);
  };

  // Helper functions for paste handling
  const extractClipboardData = useCallback(
    (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData?.getData('text');
      if (!clipboardData) {
        return null;
      }

      const cleanPaste = clipboardData.replace(/\D/g, '').slice(0, length);
      return cleanPaste || null;
    },
    [length]
  );

  const createInputValues = useCallback(
    (pasteData: string) => {
      const newValues: InputValuesProps = {};
      for (let i = 0; i < length; i++) {
        newValues[`input${i + 1}`] = pasteData[i] || '';
      }
      return newValues;
    },
    [length]
  );

  const scheduleSubmit = useCallback(
    (pasteData: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        submitPinOnce(pasteData);
        timeoutRef.current = null;
      }, 0);
    },
    [submitPinOnce]
  );

  useEffect(() => {
    const handlePasteAnywhere = (event: ClipboardEvent) => {
      if (loading) {
        return;
      }

      const pasteData = extractClipboardData(event);
      if (!pasteData) {
        return;
      }

      const newValues = createInputValues(pasteData);
      setInputValues(newValues);

      if (!loading) {
        scheduleSubmit(pasteData);
      }
    };

    window.addEventListener('paste', handlePasteAnywhere);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener('paste', handlePasteAnywhere);
    };
  }, [loading, createInputValues, extractClipboardData, scheduleSubmit]);

  return (
    <div className={cn('flex flex-col items-center')} data-testid="otp-input">
      <div
        className={cn('flex space-x-1', className)}
        data-testid="otp-field"
        id="OTPInputGroup"
      >
        {Array.from({ length }).map((_, idx) => {
          const inputId = `input${idx + 1}`;
          return (
            <OtpInputItem
              error={error}
              id={inputId}
              key={inputId}
              loading={loading}
              nextId={idx < length - 1 ? `input${idx + 2}` : null}
              onChange={handleInputChange}
              previousId={idx > 0 ? `input${idx}` : null}
              value={inputValues[inputId]}
            />
          );
        })}
      </div>

      {loading && <LoadingSpinner />}

      {error && !loading && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-2">
            <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full">
              <Info className="text-red-600" size={20} />
            </div>

            <p className="font-medium text-red-800 text-sm leading-5">
              {errorMessage ?? 'The entered code is incorrect.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtpInput;
